"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.MCP_TASK_POLL_MS = exports.MCP_TASK_STATUSES = exports.MCP_TASKS_EXTENSION = exports.MCP_KEEPALIVE_MS = exports.mcpListenFilterSchema = exports.MCP_NOTIFICATIONS = exports.MCP_SERVER_INFO = exports.MCP_DISCOVER_TTL_MS = exports.MCP_TOOLS_TTL_MS = exports.MCP_RESULT_TYPES = exports.mcpRequestSchema = exports.MCP_META_KEYS = exports.mcpClientMetaSchema = exports.MCP_HEADERS = exports.MCP_ERRORS = exports.MCP_SUPPORTED_VERSIONS = exports.MCP_PROTOCOL_VERSION = void 0;
exports.decodeHeaderValue = decodeHeaderValue;
const zod_1 = require("zod");
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ C51.2 · the MCP wire, defined once. `docs/plan/mcp.md` · R-42            │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * CLAUDE.md §3: *"Every request and response shape is one schema, defined once; the server
 * validates with it and the client infers from it."* The client here is somebody else's software,
 * so what this file buys is the other half — the server validates with it, and the stdio bridge we
 * ship alongside reads the same constants rather than a second copy of them.
 *
 * ```
 * ┌───────────────────────────────────────────────────────────────────────┐
 * │ ⚠️⚠️ THE REVISION IS A BUILD CONSTANT, NEVER A DEFAULT AND NEVER       │
 * │ INFERRED FROM A REQUEST. `decisions.md` R-42.                         │
 * └───────────────────────────────────────────────────────────────────────┘
 * ```
 *
 * The argument for that row is the document that needed it: `mcp.md`'s first draft, written three
 * days before this file, described a protocol that had been dead for seven weeks — an `initialize`
 * handshake, an `Mcp-Session-Id`, a resumable stream, OAuth behind Dynamic Client Registration.
 * Every one of those was removed or deprecated by this revision. A version that floats is a version
 * nobody measured.
 *
 * ⚠️ **Measured against `modelcontextprotocol.io` on 15 Sep 2026**, revision `2026-07-28` (stable).
 * `mcp.md` §0 lists every page read and is the place to update when it is measured again.
 */
exports.MCP_PROTOCOL_VERSION = '2026-07-28';
/**
 * ⚠️ **One entry, and a list rather than a scalar because the wire asks for a list.**
 * `server/discover` answers with `supportedVersions`, and so does an `UnsupportedProtocolVersion`
 * refusal — a client that asked for something we do not speak is told what we do speak, in one
 * round trip, rather than being left to probe.
 */
exports.MCP_SUPPORTED_VERSIONS = [exports.MCP_PROTOCOL_VERSION];
/* ── errors ──────────────────────────────────────────────────────────────────────────────────── */
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ ⚠️⚠️ `-32020` … `-32099` BELONGS TO THE SPECIFICATION AND WE MAY NEVER    │
 * │ ALLOCATE IN IT.                                                          │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * JSON-RPC reserves `-32000`…`-32099` for implementation-defined server errors, and this revision
 * partitions it: `-32000`…`-32019` is legacy and new implementations *should not use it at all*,
 * `-32020`…`-32099` is the specification's, and *"implementations MUST NOT emit any code from this
 * sub-range that is not defined by this specification"*.
 *
 * ⚠️ **So this product allocates nothing of its own here.** Everything we need to say that the
 * protocol has no code for is a **tool execution error** — `isError: true` with a sentence — which
 * is where a model can act on it anyway. `mcp.test.ts` asserts that every code this server can emit
 * is either a standard JSON-RPC one or one of the three below.
 */
exports.MCP_ERRORS = {
    /** JSON-RPC 2.0's own, used with their standard meanings. */
    parseError: -32700,
    invalidRequest: -32600,
    methodNotFound: -32601,
    /** ⚠️ Also *resource not found* as of this revision — `-32002` was retired and must not be sent. */
    invalidParams: -32602,
    internalError: -32603,
    /** The three the specification defines. */
    headerMismatch: -32020,
    missingRequiredClientCapability: -32021,
    unsupportedProtocolVersion: -32022,
};
/* ── headers ─────────────────────────────────────────────────────────────────────────────────── */
/**
 * ⚠️⚠️ **REQUIRED ON EVERY POST, AND THE SERVER MUST VALIDATE EACH AGAINST THE BODY.** The
 * transport mirrors these body fields into headers so an intermediary can route or rate-limit
 * without parsing JSON — and the mismatch check exists because *"different components in the
 * network rely on different sources of truth"*. A load balancer routing on the header while we
 * execute the body is the vulnerability; `-32020` is the answer.
 *
 * ⚠️ `Mcp-Name` may arrive Base64-sentinel-encoded and **must be decoded before comparing**.
 */
exports.MCP_HEADERS = {
    protocolVersion: 'mcp-protocol-version',
    method: 'mcp-method',
    name: 'mcp-name',
};
/**
 * ⚠️ **The sentinel is exact and case-sensitive**, and a client must use it even for a plain ASCII
 * value that happens to look like one — so a decoder that merely *tries* base64 would corrupt a
 * legitimate string. Match the wrapper, or take the value as-is.
 */
const BASE64_SENTINEL = /^=\?base64\?(.*)\?=$/;
function decodeHeaderValue(raw) {
    const wrapped = BASE64_SENTINEL.exec(raw);
    if (wrapped === null)
        return raw;
    return Buffer.from(wrapped[1] ?? '', 'base64').toString('utf8');
}
/* ── `_meta` ─────────────────────────────────────────────────────────────────────────────────── */
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ ⚠️⚠️ MCP IS STATELESS: EVERY REQUEST CARRIES ITS OWN VERSION AND          │
 * │ CAPABILITIES. THERE IS NO HANDSHAKE TO REMEMBER THEM FROM.                │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * *"Servers MUST NOT rely on prior requests over the same connection to establish context."* The
 * `initialize` exchange this revision removed is the thing that used to carry these, and a server
 * that cached them per connection would be correct until a client interleaved two conversations on
 * one socket.
 *
 * ⚠️⚠️ **`clientCapabilities` IS REQUIRED AND `clientInfo` IS NOT**, which is the opposite of what
 * it looks like from the outside. Capabilities decide what the server may send back — a task
 * handle, an elicitation — and *"a server MUST NOT rely on capabilities the client has not
 * declared"*. Identity is display and logging only, and the specification says twice not to make
 * security decisions with it.
 *
 * ⚠️ **A missing required field is `-32602` with HTTP 400**, not a 401 and not a 422 — the
 * specification names the code and the status.
 */
exports.mcpClientMetaSchema = zod_1.z.object({
    [`io.modelcontextprotocol/protocolVersion`]: zod_1.z.string(),
    [`io.modelcontextprotocol/clientCapabilities`]: zod_1.z.object({}).loose(),
    [`io.modelcontextprotocol/clientInfo`]: zod_1.z
        .object({ name: zod_1.z.string(), version: zod_1.z.string().optional() })
        .loose()
        .optional(),
    [`io.modelcontextprotocol/logLevel`]: zod_1.z.string().optional(),
    /* ⚠️ The one exception to the prefix rule, reserved for W3C trace context. `mcp.md` §11: threading
     * `traceparent` into the request id every audit row carries makes one action one thread across a
     * client, this API and the log. */
    traceparent: zod_1.z.string().optional(),
    tracestate: zod_1.z.string().optional(),
    baggage: zod_1.z.string().optional(),
});
exports.MCP_META_KEYS = {
    protocolVersion: 'io.modelcontextprotocol/protocolVersion',
    clientInfo: 'io.modelcontextprotocol/clientInfo',
    clientCapabilities: 'io.modelcontextprotocol/clientCapabilities',
    serverInfo: 'io.modelcontextprotocol/serverInfo',
    logLevel: 'io.modelcontextprotocol/logLevel',
    subscriptionId: 'io.modelcontextprotocol/subscriptionId',
};
/* ── the envelope ────────────────────────────────────────────────────────────────────────────── */
/**
 * ⚠️ **`id` MUST NOT be null**, unlike base JSON-RPC, and must be a string or a number. A
 * notification has no `id` at all and is answered with `202` and no body.
 */
exports.mcpRequestSchema = zod_1.z.object({
    jsonrpc: zod_1.z.literal('2.0'),
    id: zod_1.z.union([zod_1.z.string(), zod_1.z.number()]).optional(),
    method: zod_1.z.string(),
    params: zod_1.z.object({ _meta: zod_1.z.unknown().optional() }).loose().optional(),
});
/**
 * ⚠️⚠️ **EVERY RESULT CARRIES `resultType`, AND AN ABSENT ONE MEANS `complete` ONLY FOR OLDER
 * SERVERS.** We are not one, so ours is always present. `input_required` is MRTR's — C51.3's
 * proposals are the first thing in this product that will send it.
 */
exports.MCP_RESULT_TYPES = ['complete', 'input_required'];
/** How long a client may hold a tool list before asking again. One minute. */
exports.MCP_TOOLS_TTL_MS = 60_000;
/**
 * ⚠️ An hour for `server/discover`: it answers with a version list and a capability set, which
 * change only when this code does. `cacheScope: 'public'` there and nowhere else — it is the one
 * response in this server that is identical for every caller.
 */
exports.MCP_DISCOVER_TTL_MS = 3_600_000;
exports.MCP_SERVER_INFO = { name: 'forecastify', version: '1' };
/* ── subscriptions ───────────────────────────────────────────────────────────────────────────── */
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ C51.4 · the long-lived stream, which replaced the `GET` endpoint.         │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ```
 * ┌───────────────────────────────────────────────────────────────────────┐
 * │ ⚠️⚠️ THE SERVER MUST NOT SEND A NOTIFICATION TYPE THE CLIENT DID NOT   │
 * │ ASK FOR, AND MUST NOT SEND ANYTHING BEFORE THE ACKNOWLEDGEMENT.       │
 * └───────────────────────────────────────────────────────────────────────┘
 * ```
 *
 * *"The client sends a `subscriptions/listen` request with a `notifications` filter specifying
 * which event types it wants to receive. The server MUST NOT send notification types the client has
 * not explicitly requested."* And: *"the server MUST send `notifications/subscriptions/acknowledged`
 * as the first message … and MUST NOT send any notification on the subscription before it."*
 *
 * ⚠️ **The acknowledgement echoes the subset the server AGREED to honour**, not what was asked —
 * *"notification types the server does not support are omitted"*. That is what lets this server say,
 * without an error, that it has no list-changed notification to give: see `McpService.listen`.
 */
exports.MCP_NOTIFICATIONS = {
    acknowledged: 'notifications/subscriptions/acknowledged',
    toolsListChanged: 'notifications/tools/list_changed',
    promptsListChanged: 'notifications/prompts/list_changed',
    resourcesListChanged: 'notifications/resources/list_changed',
    resourceUpdated: 'notifications/resources/updated',
};
/**
 * ⚠️ **Every field optional, and an omitted one means "not subscribed"** — the specification's own
 * wording, which is why this schema has no defaults. A filter that defaulted anything to `true`
 * would send a client notifications it never asked for, which is the one thing the paragraph above
 * forbids.
 */
exports.mcpListenFilterSchema = zod_1.z.object({
    toolsListChanged: zod_1.z.boolean().optional(),
    promptsListChanged: zod_1.z.boolean().optional(),
    resourcesListChanged: zod_1.z.boolean().optional(),
    resourceSubscriptions: zod_1.z.array(zod_1.z.string()).optional(),
});
/**
 * ⚠️⚠️ **A KEEP-ALIVE IS NOT OPTIONAL ON A STREAM THAT MAY SAY NOTHING FOR AN HOUR.** *"Servers are
 * encouraged to periodically emit an SSE comment line … this keeps the connection from being closed
 * by intermediaries or client idle timeouts during quiet periods."* nginx's default
 * `proxy_read_timeout` is sixty seconds, so fifteen is the same number `GET /events/stream` picked
 * for the same reason, and the tick carries the credential re-validation the way that one does.
 */
exports.MCP_KEEPALIVE_MS = 15_000;
/* ── the Tasks extension ─────────────────────────────────────────────────────────────────────── */
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ C51.3 · `io.modelcontextprotocol/tasks`. `mcp.md` §5.1 · R-40            │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ```
 * ┌───────────────────────────────────────────────────────────────────────┐
 * │ ⚠️⚠️ THE MAPPING IS NOT AN ANALOGY. A PROPOSAL IS A DURABLE HANDLE, A  │
 * │ BOUNDED LIFETIME, A TERMINAL STATE AND A HUMAN IN THE MIDDLE.         │
 * └───────────────────────────────────────────────────────────────────────┘
 * ```
 *
 * The extension exists for *"CI pipelines, batch processing, **human approvals**"* and names
 * approval gates as a primary use. R-25's confirmation card is one, built eight months before this
 * extension was read — which is why it fits without moving anything.
 *
 * ⚠️⚠️ **AND THE SENTENCE THAT KEEPS RULE 6 TRUE: `tasks/update` IS NOT THE AUTHORIZATION.** A
 * client may send one saying the person accepted. Its content is ignored. The task completes when,
 * and only when, `agent_proposals` says a human pressed the button in Forecastify.
 */
exports.MCP_TASKS_EXTENSION = 'io.modelcontextprotocol/tasks';
/**
 * The lifecycle. ⚠️ `completed`, `failed` and `cancelled` are terminal — *"once reached, the task's
 * state does not change"* — which is the same guarantee `agent_proposals`' single-use claim gives.
 */
exports.MCP_TASK_STATUSES = [
    'working',
    'input_required',
    'completed',
    'failed',
    'cancelled',
];
/**
 * ⚠️ **Five seconds, and it is a hint rather than a floor.** A person reading a card and pressing a
 * button takes tens of seconds at best, so a tighter interval buys nothing and costs a query per
 * client per tick; a looser one makes the confirmation feel broken. `ttlMs` is the proposal's own
 * fifteen minutes and comes from the row rather than from a constant.
 */
exports.MCP_TASK_POLL_MS = 5_000;
//# sourceMappingURL=contract.js.map